import { chainOrSuspend, pyCallOrSuspend, pyNone, pyStr } from "@Sk";
import PyDefUtils from "PyDefUtils";
import {
    AnvilHookSpec,
    Component,
    ComponentConstructor,
    DropZone,
    HandleInteraction,
    PropertyDescription,
} from "@runtime/components/Component";
import { designerApi } from "@runtime/runner/component-designer-api";
import { s_add_component, s_remove_from_parent } from "@runtime/runner/py-util";
import { resendSelectedObjectBounds } from "../index";
import { BASE_MODULES } from "../python-environment";

const { DesignRichText, RichText } = BASE_MODULES["anvil"];

const mkDesignerHookSpec = (oldHookSpec: AnvilHookSpec): Partial<AnvilHookSpec> => {
    return {
        enableDropMode(dropping, flags) {
            const element = this.anvil$hooks.domElement!;
            const finalSlot = this._anvil.finalSlot;

            const dzs: DropZone[] = [];

            const slots = this._anvil.slots as { [slotName: string]: any };

            for (const [slotName, slot] of Object.entries(slots)) {
                if (slot.hasComponents) {
                    continue;
                }
                dzs.push({
                    element: slot.domNode,
                    dropInfo: {
                        layout_properties: { slot: slotName },
                    },
                });
            }
            if (dzs.length === 0) {
                // add a final slot
                element.appendChild(finalSlot);
                dzs.push({
                    element: finalSlot,
                    dropInfo: {},
                });
            }
            return dzs;
        },
        disableDropMode() {
            this._anvil.finalSlot.remove();
        },
        updateDesignName(name) {
            this._anvil.designName = name;
            this._anvil.updateDesignName(this);
        },
        getInteractions() {
            const contentProp = this._anvil.propTypes.find(({ name }: PropertyDescription) => name === "content");
            return [
                {
                    type: "whole_component",
                    title: "Edit text",
                    icon: "edit",
                    callbacks: {
                        execute: () => {
                            const domNode = this.anvil$hooks.domElement as HTMLDivElement;
                            const content = this._anvil.getPropJS("content");
                            const editableEl = document.createElement("div");
                            editableEl.style.whiteSpace = "pre";
                            editableEl.textContent = content;
                            domNode.replaceChildren(editableEl);
                            setTimeout(resendSelectedObjectBounds);
                            designerApi.startInlineEditing(this, contentProp, editableEl, {
                                onFinished: () => {
                                    const content = editableEl.textContent;
                                    domNode.replaceChildren();
                                    this._anvil.setPropJS("content", content);
                                    this._anvil.updateDesignName?.(this);
                                    setTimeout(resendSelectedObjectBounds);
                                },
                            });
                        },
                    },
                    default: true,
                },
            ];
        },
        updateLayoutProperties(forChild, updates) {
            // expecting either slot or width, but not both...
            if ("slot" in updates) {
                const { slot } = updates;
                return chainOrSuspend(
                    pyCallOrSuspend(Sk.abstr.gattr(forChild, s_remove_from_parent), []),
                    () => pyCallOrSuspend(Sk.abstr.gattr(this, s_add_component), [forChild], ["slot", new pyStr(slot)]),
                    () => ({ slot })
                );
            } else if ("width" in updates) {
                const { width } = updates;
                const el = forChild.anvil$hooks.domElement;
                el?.parentElement?.style.setProperty("width", PyDefUtils.cssLength(width));
                if (width == null || width === "") {
                    return {};
                }
                return { width };
            } else {
                // we don't support other updates
                return {};
            }
        },
        getContainerDesignInfo(forChild: Component) {
            const oldHandles = this._anvil.getHandles(forChild);
            return {
                layoutPropertyDescriptions: [
                    {
                        name: "slot",
                        type: "string",
                        description: "The name of the content slot where the component will be placed",
                    },
                    {
                        name: "width",
                        type: "number",
                        description: "The width for an element that is not horizontally self-sizing",
                    },
                ],
                interactions: oldHandles.map((h: any): HandleInteraction => {
                    h.pyChild = forChild;
                    let grabData: any = null;
                    return {
                        type: "handle",
                        position: "right",
                        direction: "x",
                        callbacks: {
                            grab: () => {
                                grabData = this._anvil.handleGrab(h, 0, 0);
                            },
                            drag: (dx, dy) => {
                                this._anvil.handleDrag(h, grabData, dx, dy, false);
                            },
                            drop: (dx, dy) => {
                                const { layoutProperties } = this._anvil.handleDrop(h, grabData, dx, dy);
                                designerApi.updateComponentProperties(forChild, {}, layoutProperties["undefined"]);
                            },
                        },
                    };
                }),
            };
        },
    };
};

export const DesignRichTextV3 = Sk.misceval.buildClass(
    BASE_MODULES["anvil"],
    ($gbl, $loc) => {
        $loc["__init__"] = PyDefUtils.funcFastCall(([self]: [Component], kws) =>
            chainOrSuspend(DesignRichText.prototype.tp$init.call(self, [], kws), () => {
                const finalSlot = document.createElement("div");
                finalSlot.style.width = "100%";
                self._anvil.finalSlot = finalSlot;
                const contentProp = self._anvil.propTypes.find(({ name }: PropertyDescription) => name === "content");
                return pyNone;
            })
        );
        const oldHookSpec = RichText.prototype.anvil$hookSpec;
        $loc.anvil$hookSpec = Object.assign(Object.create(oldHookSpec), mkDesignerHookSpec(oldHookSpec));
    },
    "DesignRichTextV3",
    [DesignRichText]
) as ComponentConstructor;
